import React, { FC, useCallback, useEffect, useMemo } from "react";
import { Patient } from "@models/patient";
import { useGetCharactersQuery } from "@store/characterApi";
import Loader from "@components/Loader/Loader";
import { OnboardingFormActionContainer } from "@components/Onboarding/OnboardingForm";
import styled from "@emotion/styled";
import { useNavigate } from "react-router-dom";
import CharactersForm from "@components/CharactersForm/CharactersForm";
import { useGetProcessingStatusQuery } from "@store/photoApi";
import { PhotosProcessingStatusAlert } from "@components/PhotosProcessingStatusSnackbar";
import { Box } from "@mui/material";
import EmptyState from '@components/EmptyState/EmptyState';
import { useTranslation } from 'react-i18next';
import { useLocation } from "react-router-dom";
import { withOnboardingFlow } from "@components/Onboarding/onboardingFlow";

const POLLING_INTERVAL = 2_000;

const Container = styled.div`
  width: 100%;
`;

export interface CharactersFormProps {
  patient: Patient;
}

const OnboardingCharactersForm: FC<CharactersFormProps> = ({ patient }) => {
  const { t } = useTranslation();
  const {
    data: charactersData,
    isLoading: isLoadingCharacters,
    refetch,
  } = useGetCharactersQuery({
    patientId: patient.id,
  });
  const { data: processingData } = useGetProcessingStatusQuery(
    { patientId: patient?.id! },
    { skip: !patient, pollingInterval: POLLING_INTERVAL, refetchOnFocus: true },
  );
  const characters = useMemo(
    () => charactersData?.data || [],
    [charactersData],
  );
  const navigate = useNavigate();
  const location = useLocation();

  const next = () => {
    navigate(
      withOnboardingFlow(`/onboarding/${patient.id}/companion/patient`, location.search),
    );
  };

  const onPrevious = useCallback(() => {
    const previousUrl = patient
      ? `/onboarding/${patient.id}/pictures`
      : "/onboarding/pictures";
    navigate(withOnboardingFlow(previousUrl, location.search));
  }, [navigate, patient, location.search]);

  useEffect(() => {
    if (processingData && !processingData.pending) {
      refetch();
    }
  }, [processingData, refetch]);

  if (isLoadingCharacters) {
    return <Loader />;
  }

  return (
    <Container>
      {processingData?.pending ? (
        <Box display="flex" width="100%" marginBottom="24px">
          <PhotosProcessingStatusAlert
            count={processingData.pending}
            color="warning"
          />
        </Box>
      ) : (
        <></>
      )}
      {charactersData?.data?.length ? (
        <CharactersForm patientId={patient.id} characters={characters} />
      ) : (
        <EmptyState
          image={`${process.env.PUBLIC_URL}/images/art-faces.jpeg`}
          label={t("faces.noData")}
        />
      )}

      <OnboardingFormActionContainer
        style={{ marginTop: "64px" }}
        isSubmitting={false}
        onSubmit={next}
        onPrevious={onPrevious}
      />
    </Container>
  );
};

export default OnboardingCharactersForm;
